import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Download, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { ZoomableImage } from './ZoomableImage';
import { isImage, isPdf, isVideo, isAudio } from '../utils/fileTypeUtils';
import { apiService } from '../services/api';
import type { TicketAttachmentResponse } from '../types/tickets';

interface AttachmentPreviewModalProps {
  attachment: TicketAttachmentResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDownload: (id: string, fileName: string) => void;
  siblings?: TicketAttachmentResponse[];
  initialIndex?: number;
}

export function AttachmentPreviewModal({
  attachment,
  open,
  onOpenChange,
  onDownload,
  siblings,
  initialIndex = 0,
}: AttachmentPreviewModalProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Reset index when modal opens for a different attachment
  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex, open]);

  // Determine the active attachment (from siblings or the single prop)
  const activeAttachment = siblings && siblings.length > 0
    ? siblings[currentIndex] ?? attachment
    : attachment;

  const hasSiblings = siblings && siblings.length > 1;
  const hasPrev = currentIndex > 0;
  const hasNext = siblings ? currentIndex < siblings.length - 1 : false;

  useEffect(() => {
    if (!open || !activeAttachment.previewUrl) {
      setPreviewUrl(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setPreviewUrl(null);

    async function loadPreview() {
      try {
        const blob = await apiService.fetchAttachmentBlob(activeAttachment.previewUrl!);
        if (!cancelled) {
          const url = URL.createObjectURL(blob);
          setPreviewUrl(url);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadPreview();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeAttachment.id, activeAttachment.previewUrl]);

  // Cleanup blob URLs
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const contentType = activeAttachment.contentType;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="truncate text-base pr-6">
            {activeAttachment.fileName}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            {activeAttachment.fileSizeFormatted}
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-auto flex items-center justify-center min-h-0">
          {loading ? (
            <div className="w-full h-64 bg-muted animate-pulse rounded-lg" />
          ) : previewUrl ? (
            <>
              {isImage(contentType) && (
                <ZoomableImage src={previewUrl} alt={activeAttachment.fileName} />
              )}
              {isPdf(contentType) && (
                <iframe
                  src={previewUrl}
                  title={activeAttachment.fileName}
                  className="w-full h-[70vh] rounded border"
                />
              )}
              {isVideo(contentType) && (
                <video
                  controls
                  src={previewUrl}
                  className="max-w-full max-h-[70vh] rounded"
                />
              )}
              {isAudio(contentType) && (
                <div className="w-full p-8">
                  <audio controls src={previewUrl} className="w-full" />
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center gap-4 py-12 text-muted-foreground">
              <FileText className="h-16 w-16" />
              <p className="text-sm">Preview not available</p>
            </div>
          )}
        </div>

        <div className="flex-shrink-0 flex items-center justify-between pt-2 border-t">
          <div className="flex items-center gap-2">
            {hasSiblings && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentIndex((i) => i - 1)}
                  disabled={!hasPrev}
                  title="Previous attachment"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {currentIndex + 1} / {siblings!.length}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentIndex((i) => i + 1)}
                  disabled={!hasNext}
                  title="Next attachment"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDownload(activeAttachment.id, activeAttachment.fileName)}
          >
            <Download className="h-4 w-4 mr-1.5" />
            Download
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
